/**
 * UniqueButton — bouton universel de l'application UNIQUE.
 *
 * Variantes : primary (or solaire), secondary (contour or), ghost (discret),
 * danger (rouge — réservé aux actions destructrices).
 * Tailles : sm | md | lg.
 * Supporte icônes gauche/droite (Lucide), état de chargement et pleine largeur.
 */
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type UniqueButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type UniqueButtonSize = 'sm' | 'md' | 'lg';

export interface UniqueButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: UniqueButtonVariant;
  size?: UniqueButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const sizeClasses: Record<UniqueButtonSize, string> = {
  sm: 'text-xs px-3 py-1.5 gap-1.5',
  md: 'text-sm px-5 py-2.5 gap-2',
  lg: 'text-base px-7 py-3.5 gap-2.5',
};

const variantClasses: Record<UniqueButtonVariant, string> = {
  primary: 'unique-btn-primary',
  secondary: 'unique-btn-secondary',
  ghost: 'unique-btn-ghost',
  danger:
    'bg-[color:var(--color-urgent)] text-white font-semibold rounded-xl ' +
    'shadow-card hover:shadow-hover hover:brightness-110 transition-unique',
};

export const UniqueButton = forwardRef<HTMLButtonElement, UniqueButtonProps>(
  function UniqueButton(
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      disabled,
      className = '',
      children,
      type = 'button',
      ...rest
    },
    ref,
  ) {
    const composed = [
      'inline-flex items-center justify-center select-none',
      'font-semibold tracking-tight',
      'focus-visible:outline-none',
      sizeClasses[size],
      variantClasses[variant],
      fullWidth ? 'w-full' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        type={type}
        className={composed}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...rest}
      >
        {isLoading ? (
          <span
            className="inline-block w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin"
            aria-hidden="true"
          />
        ) : (
          leftIcon
        )}
        {children ? <span>{children}</span> : null}
        {!isLoading && rightIcon ? rightIcon : null}
      </button>
    );
  },
);
